import * as React from 'react';

import { gte } from '@lendi-ui/breakpoint';
import { Button } from '@lendi-ui/button';
import { color } from '@lendi-ui/color';
import Container from '@lendi-ui/container';
import Grid from '@lendi-ui/grid';
import { mb, mt, pb, px, py } from '@lendi-ui/spacing';
import { Body, Heading } from '@lendi-ui/typography';
import styled from 'styled-components';

import { LoanOptionsWidgetContent, LoanOptionsWidgetPrimary } from '#contentTypes/LoanOptionsWidget';

import { getBaseUrl } from '../lib/environmentFunctions';

// --------------- Widget Integration Constants provided by LoanOptions AI ---------------
const WIDGET_TOOL_SCRIPT_ID = 'loanoptions-widget-tool';
const WIDGET_TOOL_CSS_ID = 'loanoptions-widget-css';
const WIDGET_JS_URL = 'https://loanoptions-widget.s3.ap-southeast-2.amazonaws.com/v2/main/js/main.js';
const WIDGET_CSS_URL = 'https://loanoptions-widget.s3.ap-southeast-2.amazonaws.com/v2/main/css/main.css';
const PARTNER_ID = '83936107723827802171';
const SOURCE = 'Lendi';
const COUNTRY_CODE = 'AU';

const TAB_KEYS = ['home', 'car', 'personal', 'business', 'equipment'] as const;
type TabKey = typeof TAB_KEYS[number];
// --------------- Styled Components ---------------

const HeroBackground = styled.section`
  background-color: #003046;
  position: relative;
  overflow: hidden;
`;

const HeroContainer = styled(Container)`
  && {
    max-width: 1250px;
    width: calc(100% - 2rem);
  }
  padding-top: 120px;
  ${pb('xxxl')}
`;

const ContentUnit = styled(Grid.Unit)`
  display: flex;
  flex-direction: column;
  z-index: 1;
  text-align: center;
`;

const ImageUnit = styled(Grid.Unit)`
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const HeroTitle = styled(Heading)`
  color: ${color('shade.0')};
  ${mb('sm')}

  h1, h2 {
    color: ${color('shade.0')};
    margin: 0;
  }
`;

const HeroSubtitle = styled(Body)`
  color: rgba(255, 255, 255, 0.75);
  ${mb('lg')}
`;

const WidgetContainer = styled.div`
  ${mb('md')}

  #repaymentWidget {
    max-width: 520px;
    margin: auto;
  }

  @media (max-width: 555px) {
    #repaymentWidget {
      padding: 0px !important;
    }
    #loan-type-loan-box-container > div {
      flex-grow: 1;
      width: 45% !important;
    }
  }
`;

const AppointmentBox = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  ${mt('md')}
  ${py('sm')}
  ${px('md')}
  background-color: ${color('secondary.700')};
  border-radius: 8px;
  flex-direction: column;
  text-align: center;
  gap: 1rem;

  ${gte('lg')`
    flex-direction: row;
    text-align: left;
    gap: 1.25rem;
  `}
`;

const AppointmentTextGroup = styled.div`
  flex: 1;
`;

const AppointmentLabel = styled(Body)`
  display: block;
  font-weight: 700;
  color: ${color('shade.0')};
  ${mb('xxxs')}
`;

const AppointmentSubtitleText = styled(Body)`
  color: rgba(255, 255, 255, 0.75);
`;

const AppointmentCTA = styled(Button)`
  flex-shrink: 0;
  white-space: nowrap;

  && {
    border-color: ${color('primary.500')};
    color: ${color('shade.0')};

    &:hover {
      background-color: ${color('primary.500')};
      border-color: ${color('primary.500')};
      color: ${color('shade.0')};
    }
  }
`;

const HeroImage = styled.img`
  max-width: 60%;
  height: auto;
  object-fit: contain;
  transition: opacity 0.3s ease;

  ${gte('lg')`
    max-width: 100%;
    width: 100%;
  `}
`;

// --------------- Helper ---------------

const getText = (field?: { text?: string }): string => field?.text || '';

const getImageUrl = (field?: { url?: string; alt?: string; fluid?: { src?: string } }): { url: string; alt: string } => ({
  url: field?.url || field?.fluid?.src || '',
  alt: field?.alt || '',
});

/**
 * Map the LoanOptions widget DOM element IDs to our TabKey values.
 * The widget renders: loan-type-loan-box-HOME_LOAN, loan-type-loan-box-CAR_LOAN, etc.
 */
const WIDGET_ID_TO_TAB: Record<string, TabKey> = {
  'loan-type-loan-box-HOME_LOAN': 'home',
  'loan-type-loan-box-CAR_LOAN': 'car',
  'loan-type-loan-box-PERSONAL_LOAN': 'personal',
  'loan-type-loan-box-BUSINESS_LOAN': 'business',
  'loan-type-loan-box-EQUIPMENT_LOAN': 'equipment',
};

/**
 * Detect the active tab inside the LoanOptions widget by listening for clicks
 * on the known loan-type box elements within the widget container.
 */
const useActiveTab = (widgetContainerRef: React.RefObject<HTMLDivElement | null>): TabKey => {
  const [activeTab, setActiveTab] = React.useState<TabKey>('home');

  React.useEffect(() => {
    const container = widgetContainerRef.current;
    if (!container) return;

    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      // Find the closest loan-type box ancestor (or self) within the container
      const loanBox = target.closest<HTMLElement>('[id^="loan-type-loan-box-"]');
      if (loanBox && WIDGET_ID_TO_TAB[loanBox.id]) {
        setActiveTab(WIDGET_ID_TO_TAB[loanBox.id]);
      }
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [widgetContainerRef]);

  return activeTab;
};

// --------------- Component ---------------

interface PrismicLoanOptionsWidgetProps {
  component: LoanOptionsWidgetContent;
}

const PrismicLoanOptionsWidget: React.FC<PrismicLoanOptionsWidgetProps> = ({ component }) => {
  const primary: LoanOptionsWidgetPrimary = React.useMemo(() => component.primary || {}, [component.primary]);
  const widgetRef = React.useRef<HTMLDivElement>(null);
  const activeTab = useActiveTab(widgetRef);

  // Inject widget script + CSS
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    // Allow partnerId override via query param (e.g. ?partnerId=xxx)
    const params = new URLSearchParams(window.location.search);
    const partnerId = params.get('partnerId') || PARTNER_ID;

    localStorage.setItem('externalPartnerId', partnerId);
    localStorage.setItem('sourceUrl', getBaseUrl());
    localStorage.setItem('source', SOURCE);
    localStorage.setItem('countryCode', COUNTRY_CODE);

    if (!document.getElementById(WIDGET_TOOL_CSS_ID)) {
      const link = document.createElement('link');
      link.id = WIDGET_TOOL_CSS_ID;
      link.rel = 'stylesheet';
      link.href = WIDGET_CSS_URL;
      document.head.appendChild(link);
    }

    if (!document.getElementById(WIDGET_TOOL_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = WIDGET_TOOL_SCRIPT_ID;
      script.src = WIDGET_JS_URL;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // Determine which image to show based on active tab
  const imageMap: Record<TabKey, { url: string; alt: string }> = {
    car: getImageUrl(primary.image_car),
    personal: getImageUrl(primary.image_personal),
    business: getImageUrl(primary.image_business),
    equipment: getImageUrl(primary.image_equipment),
    home: getImageUrl(primary.image_home),
  };

  const currentImage = imageMap[activeTab];

  const heading = getText(primary.hero_heading) || 'The RBA made their call, time to make yours';
  const subtitle = getText(primary.hero_subtitle) || 'One 30 minute phone call with a broker could save you thousands.';
  const appointmentText = getText(primary.appointment_text) || 'Need a human touch?';
  const appointmentSubtitle = getText(primary.appointment_subtitle) || 'Go straight to booking an appointment.';
  const appointmentButtonText = getText(primary.appointment_button_text) || 'BOOK AN APPOINTMENT';
  const appointmentButtonLink = primary.appointment_button_link?.url || '/book-appointment/';

  return (
    <HeroBackground>
      <HeroContainer>
        <Grid gutter="xl" valign="center">
          <ContentUnit size={{ xs: 1, lg: 0.5 }}>
            <HeroTitle size="xl">{heading}</HeroTitle>
            <HeroSubtitle size="lg">{subtitle}</HeroSubtitle>

            <WidgetContainer ref={widgetRef}>
              <div id="repaymentWidget" />
            </WidgetContainer>

            <AppointmentBox>
              <AppointmentTextGroup>
                <AppointmentLabel size="md">{appointmentText}</AppointmentLabel>
                <AppointmentSubtitleText size="sm">{appointmentSubtitle}</AppointmentSubtitleText>
              </AppointmentTextGroup>
              <AppointmentCTA
                variant="secondary"
                size="md"
                href={appointmentButtonLink}
                isInverse
              >
                {appointmentButtonText}
              </AppointmentCTA>
            </AppointmentBox>
          </ContentUnit>

          <ImageUnit size={{ xs: 1, lg: 0.5 }}>
            {currentImage.url && (
              <HeroImage
                key={activeTab}
                src={currentImage.url}
                alt={currentImage.alt || `${activeTab} loan illustration`}
              />
            )}
          </ImageUnit>
        </Grid>
      </HeroContainer>
    </HeroBackground>
  );
};

export default PrismicLoanOptionsWidget;
